import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, FakeText, Typography } from '@totalsoft/rocket-ui';
import { Grid, TableBody, TableRow, TableHead, TableCell, Icon } from '@mui/material';
import InsurerDetails from './InsurerDetails';
import { sequence, get } from '@totalsoft/change-tracking-react';
import AddIcon from '@mui/icons-material/Add';
import { StyledTable, StyledHeaderCell, StyledGrayIcon } from 'components/common/Styles';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_INSURANCE_CONVENTIONS_WRITE } from 'constants/rightsCodes';
import FlexBox from 'components/common/FlexBox';

const InsuranceConventionInsurerComponent = ({
  insurersLens,
  insurers,
  onNewInsurer,
  onSaveDetail,
  onEdit,
  onCancelEditInsurer,
  onDeleteInsurer,
  productId,
  loadingInsuranceConventionInsurers,
  isNewConvention,
  validation
}) => {
  const { t } = useTranslation();
  const canWrite = usePermission(ADMIN_INSURANCE_CONVENTIONS_WRITE);

  const handleDeleteInsurer = useCallback(
    insurerId => {
      onDeleteInsurer(productId, insurerId);
    },
    [onDeleteInsurer, productId]
  );

  const hasUnsavedChanges = useMemo(() => {
    const unsavedChanges = (insurersLens |> sequence).find(x => (x.insurerId |> get) === null);
    return Boolean(unsavedChanges);
  }, [insurersLens]);

  return (
    <>
      {isNewConvention ? (
        <Grid container flexDirection='column' gap={1} p={2}>
          <FlexBox gap={1} alignItems='center' justifyContent='center'>
            <Icon fontSize='small' color='primary'>
              error
            </Icon>
            <Typography color='textPrimary'>{t('InsuranceConventions.Details.Warning')}</Typography>
          </FlexBox>
        </Grid>
      ) : (
        <StyledTable>
          <TableHead>
            <TableRow>
              <StyledHeaderCell title={t('InsuranceConventions.Details.Insurers.Insurer')}>
                {t('InsuranceConventions.Details.Insurers.Insurer')}
              </StyledHeaderCell>
              <StyledHeaderCell title={t('InsuranceConventions.Details.Insurers.DefaultConvention')}>
                {t('InsuranceConventions.Details.Insurers.DefaultConvention')}
              </StyledHeaderCell>
              <StyledHeaderCell align='right'>
                <Button
                  id='Insurer_AddValue'
                  startIcon={<AddIcon />}
                  onClick={onNewInsurer}
                  sx={{ whiteSpace: 'nowrap' }}
                  disabled={hasUnsavedChanges || !canWrite}
                >
                  {t('Add')}
                </Button>
              </StyledHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {!isNewConvention && loadingInsuranceConventionInsurers && (
                <TableCell colSpan={3}>
                  <FakeText lines={5} />
                </TableCell>
              )}
              {!isNewConvention && !loadingInsuranceConventionInsurers && (insurersLens |> sequence).length === 0 && (
                <TableCell colSpan={3}>
                  <FlexBox gap={1} alignItems='center' justifyContent='center'>
                    <StyledGrayIcon lsGrayIcon fontSize='small'>
                      error
                    </StyledGrayIcon>
                    <Typography color='textSecondary'>{t('InsuranceConventions.Details.AddValues')}</Typography>
                  </FlexBox>
                </TableCell>
              )}
            </TableRow>
            {!isNewConvention &&
              !loadingInsuranceConventionInsurers &&
              insurersLens &&
              (insurersLens |> sequence).map((insurer, index) => {
                return (
                  <InsurerDetails
                    key={index}
                    index={index}
                    insurer={insurer}
                    insurers={insurers}
                    onEdit={onEdit}
                    onCancelEditInsurer={onCancelEditInsurer}
                    onSaveDetail={onSaveDetail}
                    onDelete={handleDeleteInsurer}
                    validation={validation}
                  />
                );
              })}
          </TableBody>
        </StyledTable>
      )}
    </>
  );
};

InsuranceConventionInsurerComponent.propTypes = {
  insurersLens: PropTypes.object,
  insurers: PropTypes.array,
  onNewInsurer: PropTypes.func,
  onSaveDetail: PropTypes.func,
  onEdit: PropTypes.func,
  onCancelEditInsurer: PropTypes.func,
  onDeleteInsurer: PropTypes.func,
  productId: PropTypes.number,
  loadingInsuranceConventionInsurers: PropTypes.bool,
  isNewConvention: PropTypes.bool,
  validation: PropTypes.object
};

export default InsuranceConventionInsurerComponent;
